import React, { useEffect, useState } from "react";
import Button from "../../../components/Buttons/Button";
import CheckboxListField from "../../../components/Fields/CheckboxListField";
import RadioButtonListField from "../../../components/Fields/RadioButtonListField";
import SelectListField from "../../../components/Fields/SelectListField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import SpinnerOverlay from "../../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from "../../../contexts/authContext";
import { useNotifications } from "../../../contexts/notificationContext";
import ItemRevisionModel from "../../../models/ItemRevisionModel";
import ProjectItemRevisionModel from "../../../models/ProjectItemRevisionModel";
import WorkflowDefinitionModel from "../../../models/WorkflowDefinitionModel";
import DataService from "../../../services/data.service";
import { ProjectPopupProps } from "../../../types/PopupProps";
import { Result } from "../../../types/Result";
import SelectProjectItemsControl from "../Components/SelectProjectItemsControl";

export interface TasksForMultipleItemsModel {
    projectId: number
    taskId: number
    startWorkflowDefinitionId?: number
    workflowExecutionTypeId?: number
    moveToProjectId?: number
    functionalRoleId?: number
    currentEmployeeId?: number
    employeeId?: number
    colorIds?: number[]
    sizeSetId?: number
    sizeIds?: number[]
    businessPartnerTypeId?: number
    businessPartnerId?: number
    externalSystemId?: number
    itemRevisionIds: number[]
}

const TasksForMultipleItemsPopup: React.FC<ProjectPopupProps> = ({ show, onHide, projectId, projectTypeId }) => {
    const [formData, setFormData] = useState<TasksForMultipleItemsModel>();
    //const [selectedItems, setSelectedItems] = useState<ProjectItemRevisionModel[]>([])
    const [showSpinner, setShowSpinner] = useState(false)
    const [showSubItems, setShowSubItems] = useState(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedForProjectType, isAuthorizedForItemType } = useAuth()
    const [itemsUrl, setItemsUrl] = useState<string>()

    const workflowDefinitions = useSelectListOptions('api/v1/selectlist/ProjectItemWorkflowDefinitions?projectTypeId=' + projectTypeId, '-- Select workflow');
    const workflowExecutionTypes = useSelectListOptions(`api/v1/selectlist/WorkflowExecutionTypes`, '-- Select execution type');
    const projects = useSelectListOptions('api/v1/selectlist/Projects?activeOnly=true&projectTypeId=' + projectTypeId, '-- Select project');
    const functionalRoles = useSelectListOptions('api/v1/selectlist/FunctionalRolesForItemsInProject?projectId=' + projectId, '-- Select role');
    const currentEmployees = useSelectListOptions(`api/v1/selectList/EmployeesForFunctionalRoleInProject?projectId=${projectId}&functionalRoleId=${formData?.functionalRoleId}`, '-- select employee')
    const employees = useSelectListOptions('api/v1/selectlist/EmployeesForFunctionalRole?functionalRoleId=' + formData?.functionalRoleId, '-- Select employee');
    const colors = useSelectListOptions('api/v1/selectlist/Colors?activeOnly=true');
    const sizesets = useSelectListOptions('api/v1/selectlist/Sizesets?activeOnly=true', '-- Select sizeset');
    const sizes = useSelectListOptions(`api/v1/selectlist/Sizes?sizeSetId=${formData?.sizeSetId}&activeOnly=true`);
    const businessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes');
    const businessPartners = useSelectListOptions(`api/v1/selectlist/BusinessPartners?businessPartnerTypeId=${formData?.businessPartnerTypeId}`, '-- Select Business-Partner');
    const externalSystems = useSelectListOptions(`api/v1/selectlist/Externalsystems`, '-- Select External System');

    const tasks = [{ value: 0, label: '-- Select task' }]
    if (isAuthorizedForProjectType(ApplicationFunctions.StartItemWorkflow, projectTypeId))
        tasks.push({ value: 1, label: 'Start item Workflows' })
    if (isAuthorizedForProjectType(ApplicationFunctions.UpdateProject, projectTypeId)) {
        tasks.push({ value: 2, label: 'Move items to another project' })
    }
    if (isAuthorizedForItemType(ApplicationFunctions.UpdateItemRevision, 0)) {
        tasks.push({ value: 3, label: 'Replace user in items' })
        tasks.push({ value: 4, label: 'Add Colors to items' })
        tasks.push({ value: 5, label: 'Add Sizes to items' })
        tasks.push({ value: 6, label: 'Add Business-Partner to items' })
    }
    if (isAuthorizedForItemType(ApplicationFunctions.ExportItem, 0)) {
        tasks.push({ value: 7, label: 'Export items' })
    }


    useEffect(() => {
        const defaultFormData = {
            projectId: projectId,
            taskId: 0,
            workflowExecutionTypeId: 1,
            functionalRoleId: 0,
            currentEmployeeId: 0,
            colorIds: [],
            sizeSetId: 0,
            sizeIds: [],
            businessPartnerTypeId: 0,
            businessPartnerId: 0,
            externalSystemId: 0,
            projectItemRevisionIds: [],
            itemRevisionIds: []
        }
        setFormData(defaultFormData);
    }, [show, projectId]);

    useEffect(() => {
        if (formData) {
            switch (formData.taskId) {
                case 1:
                    setItemsUrl(`api/v1/ItemRevisions/WorkflowStartableItemsByProject?projectId=${projectId}&workflowDefinitionId=${formData.startWorkflowDefinitionId}&workflowExecutionTypeId=${formData.workflowExecutionTypeId}`);
                    break;

                case 2:
                    setItemsUrl(`api/v1/ItemRevisions/ByProject/${projectId}`);
                    break;

                case 3:
                    setItemsUrl(`api/v1/ItemRevisions/ByProjectWithEmployeeInRole?projectId=${projectId}&employeeId=${formData.currentEmployeeId}&functionalRoleId=${formData.functionalRoleId}`);
                    break;

                case 4:
                    setItemsUrl(`api/v1/ItemRevisions/ByProjectWithColors/${projectId}`);
                    break;

                case 5:
                    setItemsUrl(`api/v1/ItemRevisions/ByProjectWithSizes/${projectId}`);
                    break;

                case 6:
                    setItemsUrl(`api/v1/ItemRevisions/ByProjectWithBusinessPartners/${projectId}`);
                    break;

                case 7:
                    setItemsUrl(`api/v1/ItemRevisions/ByProjectForExport?projectId=${projectId}&externalSystemId=${formData.externalSystemId}`);
                    break;

            }
        }
    }, [projectId, formData?.taskId, formData?.startWorkflowDefinitionId, formData?.workflowExecutionTypeId, formData?.currentEmployeeId, formData?.functionalRoleId, formData?.externalSystemId])

    function getWorkflowDefinition(workflowDefinitionId: number) {
        setShowSpinner(true)
        DataService
            .get<WorkflowDefinitionModel>('api/v1/WorkflowDefinitions/' + workflowDefinitionId)
            .then(response => {
                setShowSpinner(false)
                if (formData) {
                    if (response.workflowExecutionTypeId)
                        formData.workflowExecutionTypeId = response.workflowExecutionTypeId
                    else
                        formData.workflowExecutionTypeId = 1

                    if (!response.workflowExecutionTypeId)
                        setShowSubItems(true)
                    else
                        setShowSubItems(false)
                }
            })
            .catch(e => { handleException(e); setShowSpinner(false) });
    }

    function onFieldChanged(name: string, value: string): void {
        if (name === 'startWorkflowDefinitionId') {
            if (formData) {
                formData.workflowExecutionTypeId = 0
                setFormData(formData)
            }
            if (value)
                getWorkflowDefinition(Number(value))
        }
    }

    function onSelectedItemsChanges(selectedItems: ItemRevisionModel[]) {
        if (formData)
            formData.itemRevisionIds = selectedItems.map(x => x.id)
    }


    function submit() {
        setShowSpinner(true)
        DataService
            .post<Result>(`api/v1/ProjectItemRevisions/TasksForMultipleItems`, formData)
            .then(response => {
                handleResult(response);
                setShowSpinner(false)
                if (onHide)
                    onHide();
            })
            .catch(e => { handleException(e); setShowSpinner(false); });
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'taskId', label: 'Task', labelSize: 2 },
            { name: 'startWorkflowDefinitionId', label: 'Start Workflow', labelSize: 2 },
            { name: 'workflowExecutionTypeId', label: 'Execution Type', labelSize: 2 },
            { name: 'moveToProjectId', label: 'Move to Project', labelSize: 2 },
            { name: 'functionalRoleId', label: 'Role', labelSize: 2 },
            { name: 'currentEmployeeId', label: 'Replace', labelSize: 2 },
            { name: 'employeeId', label: 'Replace with', labelSize: 2 },
            { name: 'colorIds', label: 'Colors', labelSize: 2 },
            { name: 'sizeSetId', label: 'Size set', labelSize: 2},
            { name: 'sizeIds', label: 'Sizes', labelSize: 2 },
            { name: 'businessPartnerTypeId', label: 'Business-Partner type', labelSize: 2 },
            { name: 'businessPartnerId', label: 'Business-Partner', labelSize: 2 },
            { name: 'externalSystemId', label: 'Business-Partner', labelSize: 2 },
        ],
        handleSubmit: submit,
        showErrors: 'always',
        handleFieldChanged: onFieldChanged,
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    return <>
        {formData && (
            <Popup show={show} onHide={onHide} size={'xxl'} title={'Tasks for multiple Items'} >
                <Popup.Body>
                    <SelectListField {...getFieldProps('taskId')} options={tasks} />
                    {formData.taskId === 1 && <SelectListField {...getFieldProps('startWorkflowDefinitionId')} options={workflowDefinitions} />}
                    {formData.taskId === 1 && showSubItems && <SelectListField {...getFieldProps('workflowExecutionTypeId')} options={workflowExecutionTypes} />}
                    {formData.taskId === 2 && <SelectListField {...getFieldProps('moveToProjectId')} options={projects} />}
                    {formData.taskId === 3 && <SelectListField {...getFieldProps('functionalRoleId')} options={functionalRoles} />}
                    {formData.taskId === 3 && <SelectListField {...getFieldProps('currentEmployeeId')} options={currentEmployees} />}
                    {formData.taskId === 3 && <SelectListField {...getFieldProps('employeeId')} options={employees} />}
                    {formData.taskId === 4 && <CheckboxListField {...getFieldProps('colorIds')} layout='horizontal' options={colors} />}
                    {formData.taskId === 5 && <SelectListField {...getFieldProps('sizeSetId')} options={sizesets} />}
                    {formData.taskId === 5 && <CheckboxListField {...getFieldProps('sizeIds')} options={sizes} />}
                    {formData.taskId === 6 && <RadioButtonListField {...getFieldProps('businessPartnerTypeId')} layout='horizontal' options={businessPartnerTypes} />}
                    {formData.taskId === 6 && <SelectListField {...getFieldProps('businessPartnerId')} options={businessPartners} />}
                    {formData.taskId === 7 && <SelectListField {...getFieldProps('externalSystemId')} options={externalSystems} />}
                    <div style={{ marginTop: 12 }}>
                        <SelectProjectItemsControl show={true} itemsUrl={itemsUrl} formData={formData} onSelectedItemChanges={onSelectedItemsChanges} showSelectedOnly={false} />
                    </div>
                </Popup.Body>
                <Popup.Footer>
                    <Button {...getSubmitButtonProps()} text='Execute Task' />
                </Popup.Footer>
            </Popup>
        )}
        {showSpinner && <SpinnerOverlay />}
    </>
}
export default TasksForMultipleItemsPopup;